import React, { useState, useRef } from 'react';
import { useCart } from '../../contexts/CartContext';
import { 
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const CSVUploadModal = ({ isOpen, onClose }) => {
  const { addToCart } = useCart();
  const fileInputRef = useRef(null);
  
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, processing, success, error
  const [csvData, setCsvData] = useState(null);
  const [validatedProducts, setValidatedProducts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setUploadState('idle');
      setCsvData(null);
      setValidatedProducts([]);
      setErrors([]);
      setUploadProgress(0);
    }
  }, [isOpen]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setErrors(['יש להעלות קובץ CSV בלבד']);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(['הקובץ גדול מדי. מקסימום 5MB']);
      return;
    }

    uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploadState('uploading');
      setErrors([]);
      
      const formData = new FormData();
      formData.append('csvFile', file);
      
      const response = await axios.post('/api/orders/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        setUploadState('processing');
        setCsvData(response.data.csvData);
        setValidatedProducts(response.data.validProducts);
        setErrors(response.data.errors || []);
        
        if (response.data.errors && response.data.errors.length > 0) {
          setUploadState('error');
        } else {
          setUploadState('success');
        }
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setUploadState('error');
      setErrors([
        error.response?.data?.message || 'שגיאה בהעלאת הקובץ. אנא נסו שוב.'
      ]);
    }
  };

  const handleAddToCart = async () => {
    try {
      let successCount = 0;
      let failCount = 0;

      for (const product of validatedProducts) {
        try {
          await addToCart({
            id: product.id,
            name: product.name,
            price: product.price_b2b,
            image: product.images?.[0],
            sku: product.sku
          }, product.quantity);
          successCount++;
        } catch (error) {
          console.error(`Error adding ${product.sku} to cart:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        alert(`הוספו בהצלחה ${successCount} מוצרים לעגלה${failCount > 0 ? ` (${failCount} נכשלו)` : ''}`);
      }
      
      if (failCount === 0) {
        onClose();
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
      setErrors(['שגיאה בהוספת המוצרים לעגלה']);
    }
  };

  const downloadTemplate = () => {
    const template = `SKU,כמות,הערות
PER001,5,
PER002,10,דחוף
PER003,2,`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_order.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">העלאת הזמנה בקובץ CSV</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto overscroll-contain flex-1">
          {uploadState === 'idle' && (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">הוראות שימוש:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• הקובץ חייב להיות בפורמט CSV</li>
                  <li>• עמודות נדרשות: SKU, כמות</li>
                  <li>• עמודה אופציונלית: הערות</li>
                  <li>• מקסימום 1000 שורות</li>
                  <li>• גודל מקסימלי: 5MB</li>
                </ul>
              </div>

              {/* Template Download */}
              <div className="text-center">
                <button
                  onClick={downloadTemplate}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentTextIcon className="h-5 w-5 ml-2" />
                  הורד תבנית CSV
                </button>
              </div>

              {/* File Upload Area */}
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">גרור קובץ CSV או לחץ להעלאה</p>
                <p className="text-sm text-gray-500">תומך בקבצי CSV בלבד</p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {uploadState === 'uploading' && (
            <div className="text-center py-8">
              <CloudArrowUpIcon className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-bounce" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">מעלה קובץ...</h3>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500">{uploadProgress}% הושלם</p>
            </div>
          )}

          {uploadState === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">מעבד נתונים...</h3>
              <p className="text-sm text-gray-500">בודק מוצרים ומתאמת מלאי</p>
            </div>
          )}

          {(uploadState === 'success' || uploadState === 'error') && (
            <div className="space-y-6">
              {/* Results Summary */}
              <div className={`p-4 rounded-lg ${uploadState === 'success' ? 'bg-green-50' : 'bg-yellow-50'}`}>
                <div className="flex items-center">
                  {uploadState === 'success' ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500 ml-2" />
                  ) : (
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 ml-2" />
                  )}
                  <h3 className={`font-medium ${uploadState === 'success' ? 'text-green-900' : 'text-yellow-900'}`}>
                    {uploadState === 'success' ? 'הקובץ עובד בהצלחה!' : 'הקובץ עובד עם שגיאות'}
                  </h3>
                </div>
                <div className={`mt-2 text-sm ${uploadState === 'success' ? 'text-green-800' : 'text-yellow-800'}`}>
                  <p>מוצרים תקינים: {validatedProducts.length}</p>
                  {errors.length > 0 && <p>שגיאות: {errors.length}</p>}
                </div>
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-2">שגיאות:</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {errors.slice(0, 10).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                    {errors.length > 10 && (
                      <li className="font-medium">...ועוד {errors.length - 10} שגיאות</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Valid Products */}
              {validatedProducts.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">מוצרים לא הוספה לעגלה ({validatedProducts.length}):</h4>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-right py-2">SKU</th>
                          <th className="text-right py-2">שם המוצר</th>
                          <th className="text-right py-2">כמות</th>
                          <th className="text-right py-2">מחיר</th>
                          <th className="text-right py-2">סה"כ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validatedProducts.map((product, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2 font-mono">{product.sku}</td>
                            <td className="py-2">{product.name}</td>
                            <td className="py-2">{product.quantity}</td>
                            <td className="py-2">₪{product.price_b2b}</td>
                            <td className="py-2 font-medium">₪{(product.price_b2b * product.quantity).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-gray-300">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>סה"כ:</span>
                      <span>₪{validatedProducts.reduce((sum, p) => sum + (p.price_b2b * p.quantity), 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - Sticky */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            ביטול
          </button>
          
          {uploadState === 'idle' && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              בחר קובץ
            </button>
          )}
          
          {(uploadState === 'success' || uploadState === 'error') && validatedProducts.length > 0 && (
            <button
              onClick={handleAddToCart}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              הוסף {validatedProducts.length} מוצרים לעגלה
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVUploadModal;

